import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import { LinearProgress } from '@mui/material';
import MainButton from '../../../common/buttons/MainButton';
import { distanceDateFormat } from '../../../../utils/helpers';
import colors from '../../../../scss/colors';
import { useWindowSize } from '../../../../utils/hooks';
import { responsiveSizeTimesheet } from '../../../../utils/constants';
import NotificationModal from '../../../common/ModalWindows/NotificationModal';
import classes from './ApproverComment.module.scss';

const commentStatusesEnum = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected'
};

const BasicComment = ({ commentItem, clickHandler = () => {}, children }) => {
  const { t } = useTranslation();

  return (
    <div role="presentation" key={commentItem.Id} className={classes.comment} onClick={clickHandler}>
      <p>
        <span>{`${commentItem?.User?.FirstName} ${commentItem?.User?.LastName}`}</span>&nbsp;&nbsp;
        <span>{`${t('common.general.commented')} ${distanceDateFormat(commentItem.CreatedDate, true)}`}</span>
      </p>
      <span className={classes.reasonTxt}>{commentItem.Reason}</span>
      {children}
    </div>
  );
};

const EditableComment = (props) => {
  const {
    comment,
    disabled = false,
    focused = true,
    onFocus = () => {},
    onChange,
    cancelAction,
    confirmAction,
    labelForConfirm
  } = props;
  const { t } = useTranslation();

  return (
    <>
      <textarea
        placeholder={t('dashboard.timesheet.reasonforrejection')}
        disabled={disabled}
        onFocus={onFocus}
        className={classes.comment}
        value={comment}
        onChange={onChange}
      />
      {focused && (
        <div className={classes.btnsContainer}>
          <MainButton size="small" variant="primary" outlined rounded action={cancelAction}>
            {t('common.general.cancel')}
          </MainButton>
          <MainButton size="small" variant={comment ? 'primary' : 'disabled'} rounded action={confirmAction}>
            {labelForConfirm}
          </MainButton>
        </div>
      )}
    </>
  );
};

const EditCommentModal = ({ config }) => {
  const { t } = useTranslation();

  const [currentComment, setCurrentComment] = useState(config.comment);

  return (
    <div className={classes.mobileModalContainer}>
      <textarea
        value={currentComment}
        onChange={(e) => setCurrentComment(e.target.value)}
        placeholder={t('common.general.comment')}
      />
      <div className={classes.modalBtnsContainer}>
        <MainButton size="small" variant="primary" outlined rounded action={config.cancelAction} forceWidth="100%">
          {t('common.general.cancel')}
        </MainButton>
        <MainButton
          size="small"
          variant={currentComment ? 'primary' : 'disabled'}
          rounded
          action={() => config.confirmAction(currentComment)}
          forceWidth="100%"
        >
          {config.labelForConfirm}
        </MainButton>
      </div>
    </div>
  );
};

const ApproverComment = ({ disabled, isApprover, extraClass, comments, saveComment, commentSaving }) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();

  const [comment, setComment] = useState('');
  const [existComment, setExistComment] = useState('');
  const [focused, setFocused] = useState(false);
  const [editCommentId, setEditCommentId] = useState(null);
  const [openModal, setOpenModal] = useState(null);

  useEffect(() => {
    if (width < responsiveSizeTimesheet && (existComment || editCommentId))
      setOpenModal({
        comment: existComment,
        cancelAction: () => {
          setExistComment('');
          setEditCommentId(null);
          setOpenModal(null);
        },
        confirmAction: (newComment) => {
          saveComment(newComment, editCommentId);
          setExistComment('');
          setEditCommentId(null);
          setOpenModal(null);
        },
        labelForConfirm: t('common.general.update')
      });
  }, [existComment, editCommentId]);

  return (
    <div className={`${extraClass} ${classes.container}`}>
      {commentSaving && <LinearProgress sx={{ width: '100%', borderRadius: '4px' }} />}
      {isApprover &&
        !disabled &&
        (comments.length === 0 ||
          comments.every((existingComment) => existingComment.ActionTaken !== commentStatusesEnum.PENDING)) &&
        (width > responsiveSizeTimesheet ? (
          <EditableComment
            comment={comment}
            disabled={disabled}
            focused={focused}
            onFocus={() => setFocused(true)}
            onChange={(e) => setComment(e.target.value)}
            cancelAction={() => {
              setComment('');
              setFocused(false);
            }}
            confirmAction={() => saveComment(comment)}
            labelForConfirm={t('common.general.save')}
          />
        ) : (
          <div
            role="presentation"
            className={`${classes.comment} ${classes.mobile}`}
            onClick={() => {
              if (!disabled) {
                setOpenModal({
                  comment,
                  cancelAction: () => {
                    setComment('');
                    setFocused(false);
                    setOpenModal(null);
                  },
                  confirmAction: (newComment) => {
                    saveComment(newComment);
                    setOpenModal(null);
                  },
                  labelForConfirm: t('common.general.save')
                });
              }
            }}
          >
            <span className={classes.placeholder}>{t('dashboard.timesheet.reasonforrejection')}</span>
          </div>
        ))}
      {comments.length > 0 &&
        comments.map((commentItem) =>
          editCommentId === commentItem.Id && !disabled && width > responsiveSizeTimesheet ? (
            <EditableComment
              key={commentItem.Id}
              comment={existComment}
              onChange={(e) => setExistComment(e.target.value)}
              cancelAction={() => {
                setExistComment('');
                setEditCommentId(null);
              }}
              confirmAction={() => {
                saveComment(existComment, editCommentId);
                setEditCommentId(null);
              }}
              labelForConfirm={t('common.general.update')}
            />
          ) : (
            <BasicComment
              key={commentItem.Id}
              commentItem={commentItem}
              clickHandler={() => {
                if (width < responsiveSizeTimesheet && commentItem.ActionTaken === commentStatusesEnum.PENDING) {
                  setEditCommentId(commentItem.Id);
                  setExistComment(commentItem.Reason);
                }
              }}
            >
              {/* eslint-disable-next-line max-len */}
              {commentItem.ActionTaken === commentStatusesEnum.PENDING && !disabled && width > responsiveSizeTimesheet && (
                <EditIcon
                  onClick={() => {
                    setEditCommentId(commentItem.Id);
                    setExistComment(commentItem.Reason);
                  }}
                  sx={{ fontSize: '20px', color: colors.NAVY_BLUE }}
                />
              )}
            </BasicComment>
          )
        )}
      {openModal && (
        <NotificationModal
          paddingX="0px"
          backgroundColor={colors.PAGE_BACKGROUND}
          open={openModal}
          onClose={() => {
            setComment('');
            setExistComment('');
            setEditCommentId(null);
            setOpenModal(null);
          }}
        >
          <EditCommentModal config={openModal} />
        </NotificationModal>
      )}
    </div>
  );
};

export default ApproverComment;
